import { QueryKey, useQuery } from '@tanstack/react-query';
import { useLocale } from 'next-intl';

import { MovieDto as MovieV1Dto } from '@cineamo/api/models/movie/v1/MovieDto.types';
import { MovieDto as MovieV2Dto } from '@cineamo/api/models/movie/v2/MovieDto.types';
import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

type MovieDto = MovieV1Dto | MovieV2Dto;

import { TMDB_IMAGE_RESOLUTION } from '@/constants/tmdb-constants';

import { CountryCode } from '@/helpers/locale-helper';
import { getMovieAgeRating } from '@/helpers/movie-helper';
import {
    getShowingBackdrop,
    getShowingBackdropBlurDataImage,
    getShowingDescription,
    getShowingName,
    getShowingPoster,
    getShowingPosterBlurDataImage
} from '@/helpers/showing-helper';
import { getLanguageCodeFromString } from '@cineamo/ui/src/helpers/locale-helper';

import { useCachedMovieData, CachedMovieData, getCachedMovieData } from '@/hooks/useCachedMovieData';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

export type CachedShowingDataReturn = {
    name: string;
    description: string;
    highlightText: string;
    ageRating: string;
    backdropImageURL_ORIGINAL: string;
    backdropImageURL_W780: string;
    backdropBlurDataImage: string;
    posterImageURL_W780: string;
    posterBlurDataImage: string;
    movieData: CachedMovieData;
};

export function useCachedShowingData<TKey extends QueryKey = QueryKey>(
    showing: ShowingDto,
    showingMovie: MovieDto | null,
    deps: TKey = undefined
): CachedShowingDataReturn | null {
    const locale = useLocale();
    const domainConfiguration = useDomainConfiguration();
    const countryCode = domainConfiguration.cinema.countryCode;

    const { data: movieData } = useCachedMovieData({
        movie: showingMovie,
        countryCode: countryCode || showing?._embedded?.cinema?.countryCode,
        locale: locale
    });

    const { data } = useQuery({
        queryKey: [
            'cached-showing-data',
            `showingId-${showing?.id}`,
            `locale-${locale}`,
            movieData,
            ...(deps ?? []).map((dep) => JSON.stringify(dep))
        ],
        queryFn: () => getCachedShowingData(showing, showingMovie, countryCode, locale)
    });
    return data || null;
}

export function getCachedShowingData(
    showing: ShowingDto,
    showingMovie: MovieDto | null,
    countryCode: CountryCode,
    locale: string
): CachedShowingDataReturn {
    const movieData = getCachedMovieData({
        movie: showingMovie,
        countryCode,
        locale: locale
    });
    const ageRating = getMovieAgeRating(showingMovie, countryCode);
    const language = getLanguageCodeFromString(locale);

    return {
        name: getShowingName(showing, showingMovie, locale, countryCode),
        highlightText: showing?.highlightText,
        description: getShowingDescription(showing, showingMovie, locale),
        ageRating: ageRating,
        backdropImageURL_ORIGINAL: getShowingBackdrop(showing, showingMovie, {
            language,
            countryCode,
            usePosterAsFallback: true,
            usePlaceholder: false,
            movieImageOptions: {
                resolution: TMDB_IMAGE_RESOLUTION.ORIGINAL
            }
        }),
        backdropImageURL_W780: getShowingBackdrop(showing, showingMovie, {
            language,
            countryCode,
            usePosterAsFallback: true,
            usePlaceholder: false,
            movieImageOptions: {
                resolution: TMDB_IMAGE_RESOLUTION.W780
            }
        }),
        backdropBlurDataImage: getShowingBackdropBlurDataImage(showing, showingMovie, {
            language,
            countryCode,
            usePosterAsFallback: true,
            usePlaceholder: false
        }),
        posterImageURL_W780: getShowingPoster(showing, showingMovie, {
            language,
            countryCode,
            useBackdropAsFallback: true,
            usePlaceholder: false,
            movieImageOptions: {
                resolution: TMDB_IMAGE_RESOLUTION.W780
            }
        }),
        posterBlurDataImage: getShowingPosterBlurDataImage(showing, showingMovie, {
            language,
            countryCode,
            useBackdropAsFallback: true,
            usePlaceholder: false
        }),
        movieData: movieData
    };
}
